import React from 'react';
import { motion } from 'framer-motion';
import { Users, Heart, Award, Anchor } from 'lucide-react';

const About = () => {
  return (
    <section id="nosotros" className="py-20 relative overflow-hidden bg-deep-50">
      {/* Background with marine gradient - Dark Theme */}
      <div className="absolute inset-0 bg-gradient-to-br from-deep-100 via-deep-50 to-deep-100" />
      <div className="absolute inset-0 bg-deep-50/80" />
      
      {/* Floating elements - Dark Theme */}
      <div className="absolute top-20 left-10 w-32 h-32 bg-gradient-to-r from-ocean-500/10 to-jade-500/10 rounded-full blur-xl" />
      <div className="absolute bottom-32 right-16 w-40 h-40 bg-gradient-to-r from-jade-500/10 to-ocean-500/10 rounded-full blur-xl" />
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <motion.div 
            className="flex items-center justify-center gap-3 mb-6"
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-transparent bg-gradient-to-r from-ocean-500 to-jade-500 bg-clip-text font-heading">
              Nosotros
            </h2>
          </motion.div>
          <motion.p 
            className="text-xl text-pearl-700 max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            viewport={{ once: true }}
          >
            Una tradición familiar que trae lo mejor del mar patagónico a tu hogar
          </motion.p>
        </motion.div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
          
          {/* Story Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            <h3 className="text-3xl font-bold text-ocean-500 mb-6 font-heading">
              Una Historia de Mar y Familia
            </h3>
            
            <p className="text-lg text-pearl-700 leading-relaxed">
              Mar de Jade nació del sueño de una familia apasionada por los tesoros del océano patagónico. 
              Durante más de dos décadas, hemos trabajado directamente con pescadores locales y productores 
              artesanales para traer a tu mesa los mariscos más frescos y de mejor calidad.
            </p>
            
            <p className="text-lg text-pearl-700 leading-relaxed">
              Nuestra empresa familiar se especializa en productos marinos premium, manteniendo siempre 
              los estándares más altos de calidad y frescura. Cada producto que ofrecemos refleja nuestro 
              compromiso con la excelencia y el respeto por las tradiciones pesqueras de la Patagonia.
            </p>
            
            <p className="text-lg text-pearl-700 leading-relaxed">
              Desde ostras patagónicas hasta centollas fueguinas, cada producto cuenta la historia de 
              nuestras costas y el trabajo dedicado de generaciones de familias pescadoras que forman 
              parte de nuestra gran familia Mar de Jade.
            </p>
          </motion.div>

          {/* Values Cards */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className="grid grid-cols-1 sm:grid-cols-2 gap-6"
          >
            
            {/* Family Value - Dark Theme */}
            <motion.div
              whileHover={{ y: -5, scale: 1.02 }}
              className="bg-deep-200/60 backdrop-blur-lg p-6 rounded-2xl shadow-lg border border-ocean-500/30"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-gradient-to-r from-ocean-500 to-jade-500 rounded-lg">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <h4 className="text-xl font-semibold text-ocean-500">Empresa Familiar</h4>
              </div>
              <p className="text-pearl-700">
                Tres generaciones trabajando juntas para mantener viva la tradición marina patagónica.
              </p>
            </motion.div>

            {/* Quality Value - Dark Theme */}
            <motion.div
              whileHover={{ y: -5, scale: 1.02 }}
              className="bg-deep-200/60 backdrop-blur-lg p-6 rounded-2xl shadow-lg border border-ocean-500/30"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-gradient-to-r from-jade-500 to-ocean-600 rounded-lg">
                  <Award className="w-6 h-6 text-white" />
                </div>
                <h4 className="text-xl font-semibold text-ocean-500">Calidad Premium</h4>
              </div>
              <p className="text-pearl-700">
                Solo trabajamos con los mejores productos del mar, garantizando frescura y sabor excepcional.
              </p>
            </motion.div>

            {/* Tradition Value - Dark Theme */}
            <motion.div
              whileHover={{ y: -5, scale: 1.02 }}
              className="bg-deep-200/60 backdrop-blur-lg p-6 rounded-2xl shadow-lg border border-ocean-500/30"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-gradient-to-r from-ocean-600 to-jade-500 rounded-lg">
                  <Anchor className="w-6 h-6 text-white" />
                </div>
                <h4 className="text-xl font-semibold text-ocean-500">Tradición Marina</h4>
              </div>
              <p className="text-pearl-700">
                Respetamos y preservamos las técnicas tradicionales de pesca y preparación patagónica.
              </p>
            </motion.div>

            {/* Passion Value - Dark Theme */}
            <motion.div
              whileHover={{ y: -5, scale: 1.02 }}
              className="bg-deep-200/60 backdrop-blur-lg p-6 rounded-2xl shadow-lg border border-ocean-500/30"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-gradient-to-r from-ocean-500 to-ocean-700 rounded-lg">
                  <Heart className="w-6 h-6 text-white" />
                </div>
                <h4 className="text-xl font-semibold text-ocean-500">Pasión por el Mar</h4>
              </div>
              <p className="text-pearl-700">
                Cada producto refleja nuestro amor profundo por el océano y sus regalos naturales.
              </p>
            </motion.div>

          </motion.div>
        </div>

        {/* Call to Action - Dark Theme */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
          className="text-center bg-gradient-to-r from-ocean-500 to-jade-500 rounded-3xl p-8 text-white"
        >
          <h3 className="text-2xl sm:text-3xl font-bold mb-4 font-heading">
            Únete a Nuestra Familia Marina
          </h3>
          <p className="text-lg mb-6 text-pearl-900 max-w-2xl mx-auto">
            Descubre por qué miles de familias ya confían en Mar de Jade para llevar 
            lo mejor del mar patagónico a sus hogares.
          </p>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="bg-deep-100 text-ocean-500 px-8 py-3 rounded-full font-semibold text-lg hover:bg-deep-200 transition-colors duration-300 shadow-lg"
          >
            Conoce Nuestros Productos
          </motion.button>
        </motion.div>

      </div>
    </section>
  );
};

export default About;
